import { AxiosResponse } from "axios";
import { useMemo, useState, useRef, useEffect } from "react";
import {
  LayoutStyle,
  DisplayFlags,
  ServerListResponse,
  Comic,
  Artist,
  Article,
  Release,
  Bundle,
} from "../../types";
import { ResultsGrid } from "./ResultsGrid";

type ResultItem = Comic | Artist | Article | Release | Bundle;

interface FluidVirtualGridProps {
  gridHeightAdjustment: React.CSSProperties["height"];
  layout: { style: LayoutStyle; display: DisplayFlags };
  minCardWidth: number;
  gap?: number;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  hasNextPage: boolean;
  allPages: AxiosResponse<ServerListResponse<ResultItem>>[];
  onScroll?: (scrollableRef: HTMLDivElement) => void;
  header?: React.ReactNode;
  gridKey: string;
  fallbackMessage?: string | null;
  searchQuery?: string;
  onVisibleIndexChange?: (index: number) => void;
  restoreToIndex?: number;
}

export function FluidVirtualGrid({
  gridHeightAdjustment,
  layout,
  minCardWidth,
  gap = 24,
  isLoading,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  allPages,
  onScroll,
  header,
  gridKey,
  fallbackMessage,
  searchQuery,
  onVisibleIndexChange,
  restoreToIndex,
}: FluidVirtualGridProps) {
  const [gridWidth, setGridWidth] = useState<number | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const gridEl = gridContainerRef.current;
    if (!gridEl) return;

    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width && width > 0) {
        setGridWidth(width);
      }
    });

    ro.observe(gridEl);
    return () => ro.disconnect();
  }, []);

  const columnCount = useMemo(() => {
    if (gridWidth === null) return 1;
    return Math.max(1, Math.floor(gridWidth / (minCardWidth + gap)));
  }, [gridWidth, minCardWidth, gap]);

  // Don't render grid content until width is measured to prevent flash
  const measuredPages = gridWidth === null ? [] : allPages;

  return (
    <ResultsGrid
      key={gridKey}
      header={header}
      gridHeightAdjustment={gridHeightAdjustment ?? "325px"}
      layout={layout}
      isLoading={isLoading || gridWidth === null}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      allPages={measuredPages}
      onScroll={onScroll}
      gridContainerRef={gridContainerRef}
      gridProps={{
        templateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        gap: `${gap}px`,
      }}
      columnCountOverride={columnCount}
      gridItemsAlign="flex-start"
      gridItemWidth="100%"
      gridItemHeight="auto"
      fallbackMessage={fallbackMessage}
      searchQuery={searchQuery}
      onVisibleIndexChange={onVisibleIndexChange}
      restoreToIndex={restoreToIndex}
    />
  );
}
